import React, { useState, useEffect } from 'react';
import { ordersAPI } from '../lib/api';
import { Package } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { logger } from '../lib/logger';

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await ordersAPI.getUserOrders();
      setOrders(response.data);
    } catch (error) {
      logger.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      paid: { bg: 'bg-green-100', text: 'text-green-800', label: 'Paid' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Cancelled' },
      processing: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Processing' }
    };
    const { bg, text, label } = config[status] || config.pending;
    return <span className={`px-3 py-1 rounded text-xs font-medium ${bg} ${text}`}>{label}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12" data-testid="orders-page">
      <div className="container mx-auto px-4">
        <h1 
          className="text-4xl font-black text-primary mb-8"
          style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
        >
          My Orders
        </h1>

        {orders.length === 0 ? (
          <div className="text-center py-20 bg-white border border-border">
            <Package size={64} className="text-text_muted mx-auto mb-4" />
            <p className="text-text_muted mb-6">You haven't placed any orders yet</p>
            <Link
              to="/plans"
              className="inline-block bg-primary text-white px-8 py-3 rounded font-medium hover:bg-opacity-90"
            >
              Browse Plans
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <div key={order.order_id} className="bg-white border border-border p-6" data-testid={`order-${order.order_id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 pb-4 border-b border-border">
                  <div>
                    <div className="font-mono text-sm text-text_muted mb-1">Order ID: {order.order_id}</div>
                    <div className="text-xs text-text_muted">Placed: {new Date(order.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="mt-2 sm:mt-0">
                    {getStatusBadge(order.status)}
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-bold text-primary">{item.plan_name}</div>
                        <div className="text-sm text-text_muted">
                          {item.config.file_type} • {item.config.drawing_sets.join(', ')}
                        </div>
                      </div>
                      <div className="text-accent font-bold ml-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        R{item.total_price.toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-border">
                  <span className="font-bold">Total:</span>
                  <span className="text-xl font-bold text-accent" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    R{order.total_amount.toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;